import type { Query } from "@tanstack/react-query";
import { isTransientApiError } from "./errors";
import { queryClient } from "./queryClient";

type RefetchFailedOptions = {
  /** true = network/timeout 오류만(자동 트리거). false = 오류 종류 무관(사용자 [다시 시도하기]). */
  transientOnly: boolean;
};

/**
 * 구독 중(active)인 오류 상태 쿼리를 다시 불러온다 — 조회 실패 복구의 단일 경로.
 *
 * - active = enabled observer 가 있는 쿼리. TabShell 은 4탭을 상주 마운트하므로 보이지 않는 탭의 쿼리도 대상이다(의도).
 * - 진행 중(fetching) 쿼리는 predicate 로 제외하고 cancelRefetch:false 도 명시해 in-flight 를 끊거나 중복 호출하지 않는다.
 * - 개별 쿼리 실패로 reject 하지 않는다(결과는 쿼리 상태로 반영).
 */
export const refetchFailedQueries = ({ transientOnly }: RefetchFailedOptions): Promise<void> =>
  queryClient.refetchQueries(
    {
      type: "active",
      predicate: (query: Query) =>
        query.state.status === "error" &&
        query.state.fetchStatus !== "fetching" &&
        (!transientOnly || isTransientApiError(query.state.error)),
    },
    { cancelRefetch: false },
  );

/**
 * 자동 트리거(앱 복귀·탭 재진입)용 — 명백히 오프라인(`navigator.onLine === false`)이면 건너뛴다.
 * 아직 오프라인인데 복귀·탭 전환마다 재실패 → 실패 팝업이 반복 노출되는 것을 막는다.
 * WKWebView 의 onLine 은 셀룰러 토글에 늦게 갱신될 수 있어 신뢰가 제한적이다 — stale false 로 남아도
 * `online` 이벤트·팝업 [다시 시도하기] 경로는 gate 없이 복구한다.
 */
export const recoverTransientQueriesIfOnline = (): void => {
  if (navigator.onLine === false) return;
  void refetchFailedQueries({ transientOnly: true });
};

/**
 * 재연결(`online`)·앱 복귀(visibilitychange→visible) 시 네트워크 오류 쿼리를 자동 재조회한다.
 * iOS WKWebView 는 셀룰러 토글에 online/offline 이벤트를 안정적으로 주지 않아 TSQ refetchOnReconnect 만으로는
 * 복구되지 않는 경우가 있다(#419). 대상이 오류 쿼리뿐이라 WebView visibility 잦은 변동에도 부작용이 없다.
 * @returns cleanup
 */
export const bindQueryRecoveryTriggers = (): (() => void) => {
  const onOnline = () => {
    void refetchFailedQueries({ transientOnly: true }); // online 자체가 온라인 신호 — gate 없음
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") recoverTransientQueriesIfOnline();
  };
  window.addEventListener("online", onOnline);
  document.addEventListener("visibilitychange", onVisibilityChange);
  return () => {
    window.removeEventListener("online", onOnline);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
};
